import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseQueryValue } from '@netiva/classifieds-common';

import { MessagesSearchParams } from '../types';

const setParam = <T>(params: URLSearchParams, name: string, value?: T) => {
  if (value) {
    params.set(name, value?.toString());
  } else {
    params.delete(name);
  }
};

/**
 * The inbox keeps its state in the URL so a conversation stays linkable.
 */
export const useMessagesSearchParams = (): [
  MessagesSearchParams,
  (params: MessagesSearchParams, replace?: boolean) => void,
] => {
  const [_searchParams, _setSearchParams] = useSearchParams();

  const searchParams = useMemo<MessagesSearchParams>(
    () => ({
      advertId: parseQueryValue(_searchParams.get('advertId'), parseInt),
      conversationId: parseQueryValue(_searchParams.get('c'), parseInt),
      pageIndex: parseQueryValue(_searchParams.get('p'), parseInt, 1) - 1,
      participant: (_searchParams.get('side') as MessagesSearchParams['participant']) || undefined,
      unreadOnly: _searchParams.get('unread') === 'true',
    }),
    [_searchParams]
  );

  const setSearchParams = useCallback(
    (params: MessagesSearchParams, replace = false) => {
      const urlSearchParams = new URLSearchParams();
      if (params.pageIndex && params.pageIndex > 0) {
        setParam(urlSearchParams, 'p', params.pageIndex + 1);
      }
      setParam(urlSearchParams, 'advertId', params.advertId);
      setParam(urlSearchParams, 'side', params.participant);
      setParam(urlSearchParams, 'c', params.conversationId);
      setParam(urlSearchParams, 'unread', params.unreadOnly);

      // guard to prevent duplicate navigation
      if (urlSearchParams.toString() === _searchParams.toString()) return;

      _setSearchParams(urlSearchParams, { replace });
    },
    [_searchParams, _setSearchParams]
  );

  return [searchParams, setSearchParams];
};
